import { useCallback, useRef } from 'react'

import type { ResultOf, TadaDocumentNode, VariablesOf } from 'gql.tada'
import type { GraphQLClient } from 'graphql-request'
import * as _ from 'lodash-es'

export function denest_result<
  ResultType extends { [key: string]: unknown } | null,
  ItemType = ResultType extends { [key: string]: infer R } | null ? R : unknown,
>(result?: ResultType) {
  if (result == null) return undefined

  const denestKey = Object.keys(result)[0]

  return result[denestKey] as ItemType
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GenericTypedDocumentNode = TadaDocumentNode<any, any>
export type InputsOf<T extends GenericTypedDocumentNode> =
  VariablesOf<T>['input']

export function create_graphql_call(client: GraphQLClient) {
  return async function graphql_call<
    Document extends GenericTypedDocumentNode,
  >({
    query,
    variables,
  }: {
    query: Document
    variables?: VariablesOf<Document>
  }) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const response: ResultOf<Document> = await client.request({
        document: query,
        variables,
      })

      // mutations do not need to be denest-ed
      if (variables?.input != null) {
        type ItemType = typeof response extends {
          [key: string]: infer R
        } | null
          ? R
          : unknown
        return response as ItemType
      }

      return denest_result(response)
    } catch (error_catch: unknown) {
      process_error(error_catch)
    }
  }
}

function process_error(error_catch: unknown) {
  if (!_.isError(error_catch)) {
    const error_response = error_catch as { errors?: { message: string }[] }
    const { message } = error_response?.errors?.[0] || {
      message: 'unknown',
    }

    const error = new Error(`GraphQL error: ${message}`)

    _.forEach(error_response, (value, key) => {
      if (key === 'message') return
      // @ts-expect-error restore all extra keys for logging
      error[key] = value
    })
    throw error
  }
  // clean message string
  error_catch.message = error_catch.message.split(':')[0]

  throw error_catch
}

export function create_graphql_multi_call(client: GraphQLClient) {
  return async function graphql_call<
    Document extends GenericTypedDocumentNode,
  >({
    query,
    variables,
  }: {
    query: Document
    variables?: VariablesOf<Document>
  }) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const response: ResultOf<Document> = await client.request({
        document: query,
        variables,
      })

      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return response
    } catch (error_catch: unknown) {
      process_error(error_catch)
    }
  }
}

const timers: Record<string, Record<string, unknown>> = {}

export function reportEnableFailed({
  name,
  parts,
  timeout = 10000,
}: {
  name: string
  parts: Record<string, unknown>
  timeout?: number
}) {
  if (timers[name] == null) {
    setTimeout(() => {
      const latest_parts = timers[name]
      if (
        _.some(
          latest_parts,
          (part, key_name) => part == null && key_name != 'wallet_address', // wallet is expected in non-connected state
        )
      ) {
        console.error({
          message: `${name} still disabled`,
          ..._.map(latest_parts, (p, key) => ({ [key]: p != null })),
        })
      }
    }, timeout)
  }

  timers[name] = parts
}

export type DisableKeyWhenMissingFlag = 'missing'

export function disableKeyWhenMissing<T>(
  key: T,
): NonNullable<T> | DisableKeyWhenMissingFlag {
  return key ?? 'missing'
}

export function useIsSuccess() {
  const is_success_ref = useRef(true)
  is_success_ref.current = true

  return {
    get_isSuccess: useCallback(() => is_success_ref.current, []),
    combine_isSuccess: <T extends { isSuccess: boolean }>(results: T) => {
      is_success_ref.current &&= results.isSuccess
      return results
    },
  }
}
